// FORMATTING ONLY. Nothing in this file decides anything, and nothing in it does arithmetic on two
// server figures.
//
// That second half is deliberate. The prototype's live panel printed `impliedMarket − indicated` and
// showed a Nebraska owner "$-5,500" as a saving (WEB_PATHWAY_INVENTORY.md §4 "Worse" #3), and its
// all-time total summed REQUESTED reductions and overstated a partial win by more than double (§3 row
// 25, walked). Both were the client doing sums the server already answers. So: the reduction comes from
// `ask.estimatedReduction`, the saving from `M-1`, the totals from `v2Outcomes` — and there is no
// subtract helper here for anyone to reach for.

import type { Money } from "@contract";

const WHOLE_DOLLARS = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/** Whole dollars. The basis is carried by the type and named beside the figure where it matters. */
export function money(value: Money<string> | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return WHOLE_DOLLARS.format(value.amount);
}

/** Whole dollars for a figure that is not `Money` — an estimate the server's rule worked out (a rate on a reduction),
 *  printed as the app's `currencyString` prints a Double (Models.swift:3153: currency, no fraction digits). */
export function dollars(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return WHOLE_DOLLARS.format(value);
}

/** "assessed" and "market" are different things to an owner, and a screen that mixes them misleads. */
export function basisLabel(value: Money<string> | null | undefined): string {
  if (value === null || value === undefined) return "";
  return value.basis === "assessed" ? "assessed" : "market";
}

export function percent(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${value.toFixed(2)}%`;
}

export function shortDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === "") return "—";
  const [year, month] = iso.split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const index = Number(month) - 1;
  if (Number.isNaN(index) || months[index] === undefined) return iso;
  return `${months[index]} ’${year.slice(2)}`;
}

export function longDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === "") return "—";
  const [year, month, day] = iso.split("-");
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const index = Number(month) - 1;
  if (Number.isNaN(index) || months[index] === undefined) return iso;
  return `${months[index]} ${Number(day)}, ${year}`;
}

export function miles(value: number | null): string {
  return value === null ? "—" : `${value.toFixed(1)} mi`;
}

export function count(value: number | null): string {
  return value === null ? "—" : value.toLocaleString("en-US");
}
